import React, { useState, useEffect, useMemo } from "react";
import { useInstantSearch } from "react-instantsearch";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TriStateSwitch, { STATE_NONE, STATE_INCLUDE, STATE_EXCLUDE } from "./TriStateSwitch";

function getItemState(itemValue, refinementSet) {
  if (refinementSet.has("NOT:" + itemValue)) return STATE_EXCLUDE;
  if (refinementSet.has(itemValue)) return STATE_INCLUDE;
  return STATE_NONE;
}

// Corps — fetch direct OpenSearch, ZÉRO interférence avec InstantSearch
function FondsModalBody({
  attribute,
  searchable,
  searchablePlaceholder,
  selected,
  setSelected,
  modalLimit,
  onTotalCount,
  active,
}) {
  const { indexUiState } = useInstantSearch();
  const [rawItems, setRawItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch indépendant : ne tourne QUE quand active=true
  useEffect(() => {
    if (!active) return;

    // Regroupement : valeurs multiples d'un même attribut → OR (bool.should)
    const filters = [];
    const rf = indexUiState.refinementList || {};
    for (const [attr, values] of Object.entries(rf)) {
      if (attr === attribute || !values || values.length === 0) continue;
      const pos = [];
      const neg = [];
      for (const v of values) {
        if (typeof v === "string" && v.startsWith("NOT:")) {
          neg.push({ term: { [attr]: v.slice(4) } });
        } else {
          pos.push({ term: { [attr]: v } });
        }
      }
      if (pos.length === 1) {
        filters.push(pos[0]);
      } else if (pos.length > 1) {
        filters.push({ bool: { should: pos, minimum_should_match: 1 } });
      }
      if (neg.length > 0) {
        filters.push({ bool: { must_not: neg } });
      }
    }
    const range = indexUiState.range || {};
    for (const [attr, bounds] of Object.entries(range)) {
      const r = {};
      if (bounds.min != null) r.gte = bounds.min;
      if (bounds.max != null) r.lte = bounds.max;
      if (Object.keys(r).length > 0) filters.push({ range: { [attr]: r } });
    }
    const mustClauses = [];
    const queryText = indexUiState.query || "";
    if (queryText.trim()) {
      mustClauses.push({ multi_match: { query: queryText, fields: ["full_text", "titre", "cote.text", "auteur.text"] } });
    }
    const boolQuery = {};
    if (mustClauses.length > 0) boolQuery.must = mustClauses;
    if (filters.length > 0) boolQuery.filter = filters;
    const queryClause = Object.keys(boolQuery).length > 0 ? { bool: boolQuery } : { match_all: {} };

    const body = JSON.stringify({
      size: 0,
      query: queryClause,
      aggs: { facet: { terms: { field: "fonds", size: modalLimit } } }
    });

    let cancelled = false;
    fetch("./search-proxy.php/memovs_av_v1/_search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const buckets = data?.aggregations?.facet?.buckets || [];
        setRawItems(buckets.map(b => ({ value: b.key, label: b.key, count: b.doc_count })));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, indexUiState, attribute, modalLimit]);

  const sortedItems = useMemo(() => {
    const src = (rawItems || []).filter((it) => !it.value.startsWith("NOT:"));
    return [...src].sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase(), "fr")
    );
  }, [rawItems]);

  // Notifier le parent du nombre total (sans les NOT:)
  useEffect(() => {
    if (onTotalCount) onTotalCount(sortedItems.length);
  }, [sortedItems, onTotalCount]);

  const filteredItems = searchQuery.trim() === ""
    ? sortedItems
    : sortedItems.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleSwitchChange = (value, newState) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(value);
      next.delete("NOT:" + value);
      if (newState === STATE_INCLUDE) next.add(value);
      else if (newState === STATE_EXCLUDE) next.add("NOT:" + value);
      return next;
    });
  };

  return (
    <>
      {searchable && (
        <div className="sui-facet-search" style={{ marginBottom: "12px" }}>
          <input
            className="sui-facet-search__text-input"
            type="search"
            placeholder={searchablePlaceholder || "Rechercher..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      )}
      <div className="sui-multi-checkbox-facet">
        {filteredItems.length === 0 && searchQuery && (
          <div style={{ fontSize: "14px", color: "#666", textAlign: "center", padding: "16px 0" }}>
            Aucun résultat pour « {searchQuery} »
          </div>
        )}
        {filteredItems.map((item) => {
          const state = getItemState(item.value, selected);
          return (
            <label key={item.value} className="sui-multi-checkbox-facet__option-label">
              <div className="sui-multi-checkbox-facet__option-input-wrapper" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <TriStateSwitch value={state} onChange={(s) => handleSwitchChange(item.value, s)} />
                <span className="sui-multi-checkbox-facet__input-text">
                  {item.label}
                  {state === STATE_EXCLUDE && (
                    <span style={{ color: "#CC0000", marginLeft: "6px", fontSize: "11px" }}>exclu</span>
                  )}
                </span>
              </div>
              <span className="sui-multi-checkbox-facet__option-count">
                {item.count.toLocaleString("fr-CH")}
              </span>
            </label>
          );
        })}
      </div>
    </>
  );
}

// Shell — keepMounted, gère selected + flush au close
export default function FondsModal({
  attribute,
  searchable,
  searchablePlaceholder,
  open,
  onClose,
  modalLimit = 1000,
}) {
  const { indexUiState, setIndexUiState, refresh } = useInstantSearch();
  const [selected, setSelected] = useState(() => new Set());
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (open) {
      setSelected(new Set(indexUiState.refinementList?.[attribute] || []));
    }
  }, [open]);

  const handleClose = () => {
    setIndexUiState((prev) => {
      const arr = [...selected];
      return {
        ...prev,
        refinementList: {
          ...prev.refinementList,
          [attribute]: arr.length > 0 ? arr : undefined,
        },
      };
    });
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" keepMounted>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontSize: "1rem" }}>
          Fonds ({totalCount.toLocaleString("fr-CH")})
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh", overflow: "auto", pt: 1, display: open ? "block" : "none" }}>
        <FondsModalBody
          attribute={attribute}
          searchable={searchable}
          searchablePlaceholder={searchablePlaceholder}
          selected={selected}
          setSelected={setSelected}
          modalLimit={modalLimit}
          onTotalCount={setTotalCount}
          active={open}
        />
      </DialogContent>
    </Dialog>
  );
}
